// Service for handling CV generation
// This centralizes the API logic and provides a fallback for development environments

// Constants
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Utility function for retry logic
const retryWrapper = async (fn, maxRetries = MAX_RETRIES) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx)
      if (error.message.includes('400') || error.message.includes('401') || error.message.includes('403')) {
        throw error;
      }
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Exponential backoff
      const delay = RETRY_DELAY * Math.pow(2, attempt - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Development mode CV generation with OpenAI
const generateCVDevelopment = async (formData) => {
  // Ensure we have the API key
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  
  if (!apiKey) {
    throw new Error(
      "OpenAI API key is missing. Please check your environment variables."
    );
  }
  
  // Dynamic import to avoid bundling OpenAI in the client
  const { default: OpenAI } = await import('openai');
  
  const openai = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true // Only for development
  });

  // Test connection to OpenAI first
  if (process.env.NODE_ENV === 'development') {
    try {
      const testResponse = await openai.models.list();
      console.log(
        "OpenAI connection successful, available models:",
        testResponse.data.map(model => model.id).slice(0, 3)
      );
    } catch (testError) {
      console.error("OpenAI connection test failed:", testError);
      throw new Error(`Cannot connect to OpenAI API: ${testError.message}`);
    }
  }

  // Create prompt and call OpenAI
  const { prompt, isTranscriptBased } = createCVPrompt(formData);
  
  const completion = await openai.chat.completions.create({
    model: "gpt-4o-mini",
    messages: [
      { role: "system", content: getSystemPrompt() },
      { role: "user", content: prompt }
    ],
    temperature: 0.7,
    max_tokens: 4000,
  });

  let latexContent = completion.choices[0]?.message?.content;
  if (!latexContent) {
    throw new Error('No content received from OpenAI');
  }
  
  // Clean up LaTeX content
  latexContent = latexContent.replace(/^```latex\s*/, '').replace(/```\s*$/, '');
  const processedLatex = processLatexForRendering(latexContent);
  
  // Create URLs
  const htmlWithLatex = createHtmlWithLatex(processedLatex);
  const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(htmlWithLatex)}`;
  
  return { 
    cv: processedLatex,
    renderUrl: `${window.location.origin}/api/proxy-latex?text=${encodeURIComponent(processedLatex)}`,
    htmlRenderUrl: dataUrl
  };
};

// Production mode CV generation via API
const generateCVProduction = async (formData) => {
  const response = await fetch('/api/generate-cv', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData),
  });

  const contentType = response.headers.get('content-type') || '';
  const rawBody = await response.text();

  if (!response.ok) {
    let msg = rawBody;
    if (contentType.includes('application/json')) {
      try { 
        msg = JSON.parse(rawBody).error || msg; 
      } catch {
        // ignore parsing errors
      }
    }
    throw new Error(`HTTP ${response.status} – ${msg}`);
  }

  const result = contentType.includes('application/json')
    ? JSON.parse(rawBody)
    : { cv: rawBody };

  if (result.cv) {
    // Clean LaTeX content
    result.cv = result.cv
      .replace(/^```latex\s*/, '')
      .replace(/```\s*$/, '');
    result.cv = processLatexForRendering(result.cv);

    // Create helper URLs
    const htmlWithLatex = createHtmlWithLatex(result.cv);
    result.renderUrl =
      `${window.location.origin}/api/proxy-latex?text=${encodeURIComponent(result.cv)}`;
    result.htmlRenderUrl =
      `data:text/html;charset=utf-8,${encodeURIComponent(htmlWithLatex)}`;
  }

  return result;
};

// Main function
const generateCV = async (formData) => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  return retryWrapper(async () => {
    if (isDevelopment) {
      return await generateCVDevelopment(formData);
    } else {
      return await generateCVProduction(formData);
    }
  });
};

// Helper functions
const getSystemPrompt = () => `You are an expert CV writer specializing in LaTeX formatting. Your task is to create professional CVs using the exact LaTeX template provided. You will replace the placeholder text with actual content while maintaining the LaTeX structure perfectly. 

IMPORTANT LaTeX FORMATTING RULES:
1. For skills, do NOT use square brackets around the skills. For example: \\item Written communication (not \\item [Written communication])
2. Always escape underscores with a backslash (e.g., use \\_)
3. Always escape % signs with a backslash (e.g., use \\%)
4. Don't leave any placeholders in the final output - replace them all
5. Make sure all LaTeX commands are properly formed
6. For contact information, use plain text format with proper escaping, not commands

Output ONLY the completed LaTeX code with no additional commentary.`;

const createCVPrompt = (formData) => {
  const latexTemplate = `\\documentclass[11pt]{article}
\\usepackage[margin=1in]{geometry}  % Standard 1-inch margins for ATS compatibility
\\usepackage{parskip}  % Adds space between paragraphs
\\usepackage{helvet}   % Helvetica font (ATS-friendly)
\\renewcommand{\\familydefault}{\\sfdefault}  % Set Helvetica as default font
\\usepackage[T1]{fontenc}
\\usepackage{setspace}
\\setstretch{1.0}     % Slightly increased line spacing for readability

% ATS-FRIENDLY FORMATTING
% - Using standard font (Helvetica)
% - Font size 11pt (in range 10-12pt)
% - 1-inch margins
% - Clean section formatting
% - Consistent spacing

% Reduce spacing before and after section headings
\\usepackage{titlesec}
\\titlespacing*{\\section}{0pt}{0.5ex}{0.5ex}

\\begin{document}

%--------------------------------------------------------------------------------------
% NAME & CONTACT
%--------------------------------------------------------------------------------------
\\textbf{\\Large [FULL\\_NAME]}\\\\
[Email Address]

%--------------------------------------------------------------------------------------
% OBJECTIVE
%--------------------------------------------------------------------------------------
\\section*{Objective}
[Write a concise paragraph or two describing your professional focus, years of experience, relevant fields,
career goals, etc. Mention your background and ambitions here in a similar style.]

\\vspace{1pt}
\\rule{\\textwidth}{0.5pt}
%--------------------------------------------------------------------------------------
% EDUCATION
%--------------------------------------------------------------------------------------
\\section*{Education}

\\textbf{[University Name], [Degree/Major]} \\hfill [Year -- Year]\\\\
[Brief note about your research or specialization, if applicable]

\\textbf{[University Name], [Degree/Major]} \\hfill [Year -- Year]\\\\
[Optional: Another short note or highlight]

\\textbf{[University Name], [Degree/Major]} \\hfill [Year -- Present / Ongoing]\\\\
[Optional: Additional details on focus areas or research topics]

\\vspace{1pt}
\\rule{\\textwidth}{0.5pt}
%--------------------------------------------------------------------------------------
% KEY SKILLS
%--------------------------------------------------------------------------------------
\\section*{Key Skills}

\\begin{minipage}[t]{0.48\\textwidth}
  \\begin{itemize}
    \\setlength{\\itemsep}{0pt}
    \\setlength{\\parskip}{0pt}
    \\item [Skill 1]
    \\item [Skill 2]
    \\item [Skill 3]
    \\item [Skill 4]
    \\item [Skill 5]
  \\end{itemize}
\\end{minipage}
\\hfill
\\begin{minipage}[t]{0.48\\textwidth}
  \\begin{itemize}
    \\setlength{\\itemsep}{0pt}
    \\setlength{\\parskip}{0pt}
    \\item [Skill 6]
    \\item [Skill 7]
    \\item [Skill 8]
    \\item [Skill 9]
    \\item [Skill 10]
  \\end{itemize}
\\end{minipage}

\\vspace{1pt}
\\rule{\\textwidth}{0.5pt}
%--------------------------------------------------------------------------------------
% EXPERIENCE
%--------------------------------------------------------------------------------------
\\section*{Experience}

\\textbf{[Job Title], [Company Name]} \\hfill [Start Year -- End Year]\\\\
\\begin{itemize}
  \\setlength{\\itemsep}{0pt}
  \\setlength{\\parskip}{0pt}
  \\item [Responsibility or accomplishment 1. Emphasize achievements with data or results if possible.]
  \\item [Responsibility or accomplishment 2.]
\\end{itemize}

\\vspace{-3pt}
\\textbf{[Job Title], [Company Name]} \\hfill [Start Year -- Present]\\\\
\\begin{itemize}
  \\setlength{\\itemsep}{0pt}
  \\setlength{\\parskip}{0pt}
  \\item [Responsibility or accomplishment 1. List major contributions or tasks here.]
  \\item [Responsibility or accomplishment 2.]
  \\item [Responsibility or accomplishment 3.]
\\end{itemize}

\\vspace{1pt}
\\rule{\\textwidth}{0.5pt}
%--------------------------------------------------------------------------------------
% REFERENCES
%--------------------------------------------------------------------------------------
\\section*{References}

\\begin{itemize}
  \\setlength{\\itemsep}{0pt}
  \\setlength{\\parskip}{0pt}
  \\item [Reference Person -- Title or Relationship, Contact]
  \\item [Additional references or "Available upon request."]
\\end{itemize}

[Optional: mailto:[Email Address]]

\\end{document}`;

  const isTranscriptBased = !!formData.fullTranscript;
  
  if (isTranscriptBased) {
    return {
      isTranscriptBased,
      prompt: `
You are going to generate a professional CV in LaTeX format based on a transcript of someone describing their CV.

Transcript:
${formData.fullTranscript}

First, extract the following information from the transcript:
1. Personal Information (name, email, phone, location, professional title, citizenship if mentioned)
2. Professional Summary/Objective
3. Work Experience (with dates, company names, roles, responsibilities)
4. Education (degrees, institutions, dates)
5. Skills (technical and soft skills) - divide into Technical Skills and Creative Skills
6. Additional Information (certifications, languages, projects, memberships, references)

Secondly, focus on rewriting the transcript in a way that is more engaging and interesting to read.
1. Rewrite the professional summary to make it engaging and exciting to read.
2. Add any special achievements to the professional summary.
3. Rewrite the work experience to make it more engaging and exciting to read.
4. Rewrite the education to make it more engaging and exciting to read.
5. Rewrite the skills to make them more engaging and exciting to read.
6. Rewrite the additional information to make it more engaging and exciting to read.

Thirdly, make sure that the CV is optimised for ATS (Applicant Tracking Systems).

Next, use this EXACT LaTeX template to create the CV:

${latexTemplate}

Instructions for filling in the template:
1. Replace [FULL_NAME] with the person's full name
2. Fill in the Objective section with a professional summary based on the transcript
3. Complete the Education section with the educational background
4. Fill in the Key Skills section with relevant skills, organized into two columns:
   - First itemize environment: Technical Skills 
   - Second itemize environment: Creative Skills
5. Complete the Experience section with work history and responsibilities
6. Fill in the References section with references or "Available upon request"
7. Ensure all placeholders [like this] are replaced with actual content
8. Keep the LaTeX structure intact, only changing the content
9. Everything you write must be grounded in a fact, so unless an actual number appears in the transcript, do not make up a number.

IMPORTANT:
- For skills, don't use square brackets around the skills. For example:  \\item Written communication (not \\item [Written communication])
- Always escape underscores with a backslash (e.g., use \\_)
- Always escape % signs with a backslash (e.g., use \\%)
- Don't leave any placeholders in the final output - replace them all

!! THE MOST IMPORTANT RULE, MAKE THIS CV LOOK GOOD, AND BE VERY CREATIVE AND ADVERTISE THE PERSON AS MUCH AS POSSIBLE !!
!! ENSURE THAT THIS CV IS THE BEST CV THAT HAS EVER EXISTED !!
!! MAKE SURE TO ADVERTISE THE PERSON AS MUCH AS POSSIBLE !!
!! MAKE SURE TO OPTIMISE THE CV FOR ATS !!

Output ONLY the completed LaTeX code with no additional commentary.
`
    };
  }
  
  return {
    isTranscriptBased,
    prompt: `
You are going to generate a professional CV in LaTeX format based on the following structured information:

Personal Information:
Name: ${formData.personalInfo.firstName} ${formData.personalInfo.lastName}
Email: ${formData.personalInfo.email}
Phone: ${formData.personalInfo.phone}
Location: ${formData.personalInfo.location}
Professional Title: ${formData.personalInfo.title}

Professional Summary:
${formData.personalInfo.summary || "Experienced professional with expertise in the field."}

Work Experience:
${formData.experience}

Education:
${formData.education}

Skills:
${formData.skills}

${formData.certifications ? `Certifications:\n${formData.certifications}\n` : ''}
${formData.languages ? `Languages:\n${formData.languages}\n` : ''}
${formData.projects ? `Projects:\n${formData.projects}\n` : ''}
${formData.memberships ? `Professional Memberships:\n${formData.memberships}\n` : ''}
${formData.references ? `References:\n${formData.references}\n` : 'References:\nAvailable upon request.'}

Now, use this EXACT LaTeX template to create the CV:

${latexTemplate}

Instructions for filling in the template:
1. Replace [FULL_NAME] with "${formData.personalInfo.firstName} ${formData.personalInfo.lastName}"
2. Replace [Citizenship Status] with location and [Email Address] with the email
3. Fill in the Objective section with the professional summary
4. Complete the Education section with the educational background
5. Fill in the Key Skills section with relevant skills, organized into two columns:
   - First itemize environment: Technical Skills
   - Second itemize environment: Creative Skills
6. Complete the Experience section with work history and responsibilities
7. Fill in the References section with references or "Available upon request"
8. Ensure all placeholders [like this] are replaced with actual content
9. Keep the LaTeX structure intact, only changing the content

IMPORTANT:
- For skills, don't use square brackets around the skills. For example:  \\item Written communication (not \\item [Written communication])
- Always escape underscores with a backslash (e.g., use \\_)
- Always escape % signs with a backslash (e.g., use \\%)
- Don't leave any placeholders in the final output - replace them all

IMPORTANT NOTE ABOUT SKILLS: Organize the skills into these categories:
- Technical Skills (first itemize): 
- Creative Skills (second itemize): 

Output ONLY the completed LaTeX code with no additional commentary.
`
  };
};

// Function to process LaTeX for proper rendering
const processLatexForRendering = (latex) => {
  if (!latex) return '';
  
  // Make sure all backslashes are properly escaped
  let processed = latex;
  
  // Fix common LaTeX errors
  processed = processed
    // Ensure we don't have bracketed items in \item entries
    .replace(/\\item\s*\[(.*?)\]/g, '\\item $1')
    // Make sure all email addresses are properly escaped if they contain underscores
    .replace(/([a-zA-Z0-9._%+-]+)_([a-zA-Z0-9.]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '$1\\_$2')
    // Make sure there are no raw underscores in text (common in emails)
    .replace(/([^\\])_/g, '$1\\_');
  
  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    console.log("Processed LaTeX:", processed.substring(0, 200) + "...");
  }
  
  return processed;
};

// Function to create an HTML file with embedded LaTeX (alternative approach)
const createHtmlWithLatex = (latex) => {
  return `
<!DOCTYPE html>
<html>
<head>
  <title>CV Preview</title>
  <script src="https://cdn.jsdelivr.net/npm/pdfjs-dist@2.10.377/build/pdf.min.js"></script>
  <style>
    body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
    #pdf-viewer { width: 100%; height: 100vh; }
  </style>
</head>
<body>
  <div id="pdf-viewer"></div>
  <script>
    // Encode the LaTeX for use in URL
    const encodedLatex = encodeURIComponent(\`${latex.replace(/`/g, '\\`')}\`);
    
    // Create iframe pointing to our proxy API
    const iframe = document.createElement('iframe');
    iframe.src = window.location.origin + '/api/proxy-latex?text=' + encodedLatex;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';    
    document.getElementById('pdf-viewer').appendChild(iframe);
  </script>
</body>
</html>
  `;
};

export { generateCV };

